import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Search, ShieldCheck, Users } from "lucide-react";
import * as api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useData } from "@/hooks/useData";
import { Avatar, Badge, DataTable, Input, Loader, PageHeader, Select, TableCard } from "@/components/ui";
import { DEPARTMENT_OPTIONS } from "@/components/hrms";
import { aed, dateLabel } from "@/lib/format";
import { cn } from "@/lib/utils";

const DEPARTMENTS = DEPARTMENT_OPTIONS.slice(1).map((d) => d.value);

export default function Employees() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [dept, setDept] = useState("all");
  const { data: list } = useData(() => api.employees.list(user), [user]);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (list || []).filter((e) =>
      (dept === "all" || e.department === dept) &&
      (!needle || `${e.name} ${e.email} ${e.id} ${e.designation}`.toLowerCase().includes(needle)));
  }, [list, q, dept]);

  if (!list) return <Loader />;

  const open = (e) => navigate(`/employees/${e.id}`);
  const columns = [
    { key: "name", header: "Employee", render: (e) => (
      <div className="flex items-center gap-3 min-w-0">
        <Avatar name={e.name} />
        <div className="min-w-0">
          <p className="text-xs font-bold text-brand-blue truncate">{e.name}</p>
          <p className="text-[11px] text-slate-500 truncate">{e.email}</p>
        </div>
      </div>
    ) },
    { key: "designation", header: "Role", render: (e) => (
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-semibold">{e.designation}</span>
        {e.is_manager && <Badge tone="brand">Manager</Badge>}
      </div>
    ) },
    { key: "department", header: "Department", hide: "md", render: (e) => <Badge tone="blue" dot={false}>{e.department}</Badge> },
    { key: "doj", header: "Joined", hide: "xl", render: (e) => <span className="text-xs">{dateLabel(e.date_of_joining)}</span> },
    { key: "salary", header: "Salary (AED)", align: "right", hide: "lg", render: (e) => <span className="text-xs font-extrabold text-brand-blue tabular-nums">{aed(e.monthly_salary, 0)}</span> },
    { key: "go", header: "", align: "right", render: () => <ChevronRight size={14} className="text-slate-400 inline" /> },
  ];

  return (
    <>
      {/* Department summary tiles — click to filter, as the admin panel's role cards do */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {DEPARTMENTS.map((d) => {
          const n = list.filter((e) => e.department === d).length;
          const active = dept === d;
          return (
            <button
              key={d}
              onClick={() => setDept(active ? "all" : d)}
              className={cn("rounded-2xl p-4 text-left transition-all duration-200", active ? "shadow-neu-inset" : "shadow-neu-sm hover:-translate-y-0.5")}
            >
              <p className="text-xs font-semibold text-slate-400">{d}</p>
              <p className={cn("text-2xl font-extrabold tabular-nums mt-2", active ? "text-brand-orange" : "text-brand-blue")}>{n}</p>
            </button>
          );
        })}
      </div>

      <PageHeader>
        <div className="flex-1 min-w-[220px]">
          <Input icon={Search} placeholder="Search by name, email, ID or role…" value={q} onChange={(e) => setQ(e.target.value)} className="rounded-full" aria-label="Search employees" />
        </div>
        <Select value={dept} onChange={(e) => setDept(e.target.value)} options={DEPARTMENT_OPTIONS} className="sm:w-48" aria-label="Department" />
      </PageHeader>

      <TableCard
        heading={`${rows.length} employee${rows.length === 1 ? "" : "s"}`}
        aside={<><ShieldCheck size={13} className="text-brand-orange" /><span className="hidden sm:inline">{user.role === "manager" ? "Your direct reports" : "All active staff"}</span></>}
      >
        <DataTable columns={columns} rows={rows} onRowClick={open} empty={{ icon: Users, title: "No employees found", message: "Try adjusting your search or filters." }} />
      </TableCard>
    </>
  );
}
